import {inject, Injectable} from '@angular/core';
import {
  Manager,
  OpenMonthDataParams,
  PlanFactData,
  PlanFactTableDataGroupedByMonths,
  PreparePlanFactDataParams,
  Product,
  ProductNew,
  Report,
  ReviewerNew,
  Team, UpdateValueParams
} from "../types/table-data.types";
import {defaultPlatforms} from "../configs/default-platforms.config";
import {PlanFactApiService} from "./plan-fact-api.service";
import {dataGroupedByMonths} from "../configs/month.config";

@Injectable({
  providedIn: 'root'
})
export class PlanFactService {
  private planFactApiService: PlanFactApiService = inject(PlanFactApiService);

  public async preparePlanFactData({ monthNumber, currentProduct, selectedYear }: PreparePlanFactDataParams): Promise<PlanFactData> {
    const products: Array<Product> = [];
    const productsNew: Array<ProductNew> = await this.getProductsForMonth({monthNumber, selectedYear});

    for (const productNew of productsNew) {
      const teams: Array<Team> = [];
      const reviewersForProduct: Array<ReviewerNew> = await this.getReviewersForProduct(productNew.id);

      for (let reviewer of reviewersForProduct)  {
        const team: Team = {
          id: reviewer.id,
          reviewer: reviewer.reviewer,
          reviewerReport: (reviewer as unknown as Report),
          managers: reviewer.managers,
          managerReports: reviewer.managerPlanFacts,
          notAssignedManagerPlanFacts: reviewer.notAssignedManagerPlanFacts
        }
        teams.push(team);
      }

      const unAddedReviewers: Array<Manager> = productNew.reviewers.filter((reviewer: Manager) => {
        return !reviewersForProduct.map(r => r.reviewer.id).includes(reviewer.id);
      });

      const isCurrentProduct = productNew.id === currentProduct?.id;
      const product: Product = {
        ...isCurrentProduct ? currentProduct : {},
        id: productNew.id,
        productId: productNew.productId,
        productName: productNew.productName,
        teams: teams,
        unAddedReviewerList: unAddedReviewers,
        platforms: isCurrentProduct ? currentProduct.platforms : defaultPlatforms
      }

      products.push(product);
    }

    return { products }
  }

  public getIndexForChangedMonth(monthNumber: number): number {
    return dataGroupedByMonths.findIndex((month: PlanFactTableDataGroupedByMonths) => month.monthNumber === monthNumber);
  }

  public async openMonthData({ monthNumber, currentProduct, selectedYear }: OpenMonthDataParams): Promise<void> {
    const changedMonthIndex = this.getIndexForChangedMonth(monthNumber);
    dataGroupedByMonths[changedMonthIndex].planFactData = await this.preparePlanFactData({
      monthNumber: monthNumber,
      currentProduct: currentProduct,
      selectedYear: selectedYear
    });
  }

  public updateValue(updateValueParams: UpdateValueParams, selectedYear: number): void {
    const { managerId, property, value, month, currentProduct } = updateValueParams;
    this.planFactApiService.updateValue(managerId, property, value).subscribe({
      next: () => {
        this.openMonthData({
          monthNumber: month,
          currentProduct: currentProduct,
          selectedYear: selectedYear
        }).then()
      }
    })
  }

  private async getProductsForMonth({ monthNumber, selectedYear }: PreparePlanFactDataParams): Promise<Array<ProductNew>> {
    return new Promise((resolve, reject) => {
      this.planFactApiService.getProducts(monthNumber, selectedYear).subscribe({
        next: (products: Array<ProductNew>) => resolve(products),
        error: (error) => reject(error)
      })
    })
  }

  private async getReviewersForProduct(productPlanFactId: number): Promise<Array<ReviewerNew>> {
    return new Promise((resolve, reject) => {
      this.planFactApiService.getReviewers(productPlanFactId).subscribe({
        next: (products: Array<ReviewerNew>) => resolve(products),
        error: (error) => reject(error)
      })
    })
  }
}
